import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: ${Spacing.xxxl}px;
  text-align: center;
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  border: 1px solid ${Colors.borderLight};
  box-shadow: ${Shadows.md};
  margin: ${Spacing.xl}px;
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${Spacing.xl}px;
  color: ${Colors.danger};
`;

const ErrorTitle = styled.h2`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.lg}px;
`;

const ErrorMessage = styled.p`
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xl}px;
  max-width: 600px;
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin-top: ${Spacing.lg}px;
  padding: ${Spacing.lg}px;
  background: ${Colors.background};
  border-radius: ${BorderRadius.md}px;
  border: 1px solid ${Colors.border};
  width: 100%;
  max-width: 800px;
  text-align: left;
`;

const ErrorSummary = styled.summary`
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  cursor: pointer;
  padding: ${Spacing.sm}px;
  
  &:hover {
    color: ${Colors.primary};
  }
`;

const ErrorStack = styled.pre`
  background: ${Colors.backgroundDark};
  padding: ${Spacing.lg}px;
  border-radius: ${BorderRadius.sm}px;
  overflow-x: auto;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-top: ${Spacing.md}px;
  border: 1px solid ${Colors.border};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.xl}px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${Spacing.md}px ${Spacing.xl}px;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: ${Colors.primary};
    color: ${Colors.textOnPrimary};
    
    &:hover {
      background: ${Colors.primaryDark};
      transform: translateY(-1px);
    }
  ` : `
    background: ${Colors.surface};
    color: ${Colors.textPrimary};
    border: 2px solid ${Colors.border};
    
    &:hover {
      border-color: ${Colors.primary};
      background: ${Colors.backgroundDark};
    }
  `}
`;

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
    
    // Report to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // In a real application, you would send this to an error tracking service
    // like Sentry, Bugsnag, or similar
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    console.error('Error Report:', errorReport);
    
    // Example: Send to error tracking service
    // errorTrackingService.captureException(error, {
    //   extra: errorReport,
    //   tags: {
    //     section: 'ui',
    //     component: 'ErrorBoundary'
    //   }
    // });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            We're sorry, but something unexpected happened. Our team has been notified 
            and we're working to fix the issue. Please try refreshing the page or 
            returning to the dashboard.
          </ErrorMessage>

          <ActionButtons>
            <ActionButton variant="primary" onClick={this.handleRetry}>
              Try Again
            </ActionButton>
            <ActionButton onClick={this.handleReload}>
              Refresh Page
            </ActionButton>
            <ActionButton onClick={this.handleGoHome}>
              Go to Dashboard
            </ActionButton>
          </ActionButtons>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <ErrorSummary>Error Details (Development Only)</ErrorSummary>
              <div>
                <strong>Error:</strong> {this.state.error.message}
                <ErrorStack>
                  {this.state.error.stack}
                </ErrorStack>
                {this.state.errorInfo && (
                  <>
                    <strong>Component Stack:</strong>
                    <ErrorStack>
                      {this.state.errorInfo.componentStack}
                    </ErrorStack>
                  </>
                )}
              </div>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// Hook for functional components to trigger error boundary
export const useErrorHandler = () => {
  return (error: Error) => {
    throw error;
  };
};

// Higher-order component for easy wrapping
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorFallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={errorFallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};